import React, {useEffect, useRef, useState} from 'react';
import {
  Animated,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';

import {control, field, radius, space, type, universal} from '../../design-system/tokens';
import AccountImg from '../../images/svg/icons/HeaderAccount.svg';
import theme from '../../style/theme';

import ChevronRightImg from './assets/ChevronRight.svg';
import HamburgerImg from './assets/Hamburger.svg';
import MobileCartImg from './assets/MobileCart.svg';
import SearchIconImg from './assets/SearchIcon.svg';
import {
  AccountAvatar,
  AccountMenuProps,
  AccountPanel,
  AccountPanelMetrics,
  lineHeightOf,
} from './AccountMenu';
import {BackToStoreButton} from './BackToStoreButton';
import {chromeType} from './chromeType';
import {headerChrome} from './headerChrome';
import {heightsFor} from './headerGeometry';
import {HeaderLogo, MOBILE_LOGO_HEIGHT} from './HeaderLogo';
import {HeaderSearch} from './HeaderSearch';
import {
  Availability,
  AVAILABILITY_COPY,
  GIFT_CARD_STORE_COPY,
  HeaderActions,
  HeaderState,
  isCompactState,
  isEcomState,
  NO_STORE_COPY,
} from './headerTypes';
import {hiddenWhen, useProgress} from './motion';

export type MobileHeaderProps = {
  /**
   * default: strap, nav, search (65:4145). collapsed: strap and nav, search behind an icon
   * (77:5859). noEcom: strap, menu, logo, account (475:24534). noEcomCollapsed: strap, menu
   * and logo (77:7341). See HeaderState.
   */
  state: HeaderState;
  /** Undefined: no store selected; the strap shows NO_STORE_COPY. */
  storeName?: string;
  availability: Availability;
  cartCount: number;
  showCart: boolean;
  /** False hides search, account and cart. */
  showUtilities: boolean;
  /** False hides the hamburger (R38: header configs with `burger: false`). */
  showMenuButton: boolean;
  /** DIS-2010: an in-store-only store hides the hamburger and account (Figma 77:5858). */
  inStoreOnly: boolean;
  /** Gift-card store (R42): plain-text strap, logo not pressable. */
  giftCardStore: boolean;
  /** False leaves the strap band empty, with no press target (R37: blinker flows). */
  showStoreSelector: boolean;
  /** False hides the search field and the compact search icon; the 60 px band stays. */
  showSearch: boolean;
  actions: HeaderActions;
  /** The account panel (DIS-1907): the same rows as the desktop AccountMenu. */
  account: AccountMenuProps;
  /** The hamburger: SiteHeader opens the menu sheet (R60). */
  onMenuPress(): void;
};

// SHORTCUT: layout literals from header/mob 65:4145; no token. Upgrade path: DIS-1735 layout
// tokens for the mobile header. Strap + nav = heightsFor('mobile', variant).compact (92),
// plus the search row = heightsFor('mobile', 'ecom').full (152).
const STRAP_HEIGHT = 40;
const NAV_HEIGHT = 52;
const SEARCH_ROW_HEIGHT = 60;
// SHORTCUT: field height 40 is the Figma value; control.inputHeight is 44 (tokens README rule
// 2), and since DIS-1907 the field is an input. Upgrade path: design moves to 44.
const SEARCH_FIELD_HEIGHT = 40;
// Figma centres the field 4 above the row centre: 6 above, 14 below.
const SEARCH_TOP = (SEARCH_ROW_HEIGHT - SEARCH_FIELD_HEIGHT) / 2 - space.s4;
// SHORTCUT: icon sizes are Figma literals with no icon-size token. header/mob is a
// 7/6-scaled instance: icons 28, cart 32.667 × 28, badge 11.667 tall; menu 24, compact search
// 22, magnifier 14, chevron 16. The account icon is the codebase SVG at 24, as release/12.x
// BrandHeader draws it (not the Figma glyph 77:6036). Upgrade path: a DIS-1735 icon-size scale.
// Each SVG call site repeats the file's own viewBox, which the web svgr config strips.
const MENU_ICON = 24;
const ICON_BOX = 28;
const CART_WIDTH = 32.667;
const COMPACT_SEARCH_ICON = 22;
const SEARCH_ICON = 14;
const CHEVRON = 16;
const MOBILE_ACCOUNT_ICON = 24;
const BADGE_HEIGHT = 11.667;
const BADGE_TOP = 16.33;
/** HeaderAccount.svg's own viewBox. */
const ACCOUNT_ICON_VIEWBOX = '0 0 23.7307 24';
// SHORTCUT: no shadow token. Upgrade path: a DIS-1735 elevation token.
const SHADOW = '0px 4px 2px rgba(0, 0, 0, 0.1)';
// SHORTCUT: the account panel's column gap 22 (Figma IhAQZ4QmxWw2P3ZfjXAkEn 256:10489) is off
// the space scale (s16 / s24), as the desktop panel's 18. Upgrade path: design moves the gap
// onto the space scale.
const ACCOUNT_PANEL_GAP = 22;

/** On web the View ref is the DOM node, so `contains` tells inside from outside. */
const containsTarget = (node: unknown, target: unknown): boolean =>
  typeof (node as {contains?: unknown} | null)?.contains === 'function' &&
  (node as {contains(t: unknown): boolean}).contains(target);

/**
 * The mobile account panel (Figma IhAQZ4QmxWw2P3ZfjXAkEn 256:10489): the desktop panel at the
 * search field's 16 gutter, 346 at 375 in Figma (343 here).
 */
const mobileAccountMetrics = (headerWidth: number): AccountPanelMetrics => ({
  width: headerWidth - 2 * space.s16,
  gap: ACCOUNT_PANEL_GAP,
  introGap: space.s16,
  greeting: {
    ...type.heading.h2,
    ...lineHeightOf(type.heading.h2.fontSize),
    color: headerChrome.brandOnWhite,
  },
  item: {...chromeType.label14Medium, color: universal.black},
});

/**
 * Mobile header (320–1023): store strap, nav row (menu, logo, search icon when compact,
 * account, cart) and the search row. Ported from the header prototype (ea875fae6
 * MobileHeader.tsx) without the menu sheet: the hamburger calls `onMenuPress` (SiteHeader
 * renders the sheet, R60). The search field is a type-ahead (DIS-1907); the compact search icon
 * calls `onSearchPress` (RP9). The account button opens the account panel below the visible
 * header (DIS-1907).
 */
export const MobileHeader = ({
  state,
  storeName,
  availability,
  cartCount,
  showCart,
  showUtilities,
  showMenuButton,
  inStoreOnly,
  giftCardStore,
  showStoreSelector,
  showSearch,
  actions,
  account,
  onMenuPress,
}: MobileHeaderProps) => {
  const ecom = isEcomState(state);
  const {width: windowWidth, height: windowHeight} = useWindowDimensions();
  // The account panel spans the header, not the window: a Storybook device frame or a padded
  // story is narrower than the window. The window width stands in until the first layout.
  const [headerWidth, setHeaderWidth] = useState<number>();
  const compact = isCompactState(state);
  // With search off (a `search: false` route, blinker mode) the 60 px band is not mounted, so
  // the header is the 92 strap + nav (QA 2026-10-21: the empty band showed as a white gap).
  const searchShown = ecom && showUtilities && showSearch;
  const searchRowShown = searchShown && !compact;
  const accountShown = !inStoreOnly && state !== 'noEcomCollapsed';
  const searchProgress = useProgress(searchRowShown);
  const searchIconProgress = useProgress(searchShown && compact);
  const accountProgress = useProgress(accountShown);
  const shadowProgress = useProgress(compact);
  const storeLabel = storeName ?? NO_STORE_COPY;
  // QA 2026-10-21: the search panel dims the page below the header (Figma 346:31660), as
  // DesktopHeader's backdrop. HeaderSearch closes the panel on the backdrop's click.
  const backdropRef = useRef<View>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  // The account panel hangs from the bottom of the visible header: the search row's when it
  // shows (Figma y 152), the nav row's otherwise.
  const [accountOpen, setAccountOpen] = useState(false);
  const accountButtonRef = useRef<View>(null);
  const accountBackdropRef = useRef<View>(null);
  const accountScrollRef = useRef<ScrollView>(null);
  const accountPanelRef = useRef<View>(null);
  const accountTop = STRAP_HEIGHT + NAV_HEIGHT + (searchRowShown ? SEARCH_ROW_HEIGHT : 0);

  // A sign-in, a logout or a session expiry changes the rows; minimal compact hides the button.
  useEffect(() => setAccountOpen(false), [account.isLoggedIn, accountShown]);

  useEffect(() => {
    if (Platform.OS !== 'web' || !accountOpen) {
      return;
    }
    const onKeyDown = (event: {key?: string}) => {
      if (event.key === 'Escape') {
        setAccountOpen(false);
        // On web the ref is the DOM node; `focus` moves keyboard focus back to the button.
        (accountButtonRef.current as unknown as {focus?: () => void} | null)?.focus?.();
      }
    };
    // The button's and the backdrop's own presses close the panel: closing on pointerdown
    // would unmount the backdrop and hand the click to the page below it. Capture phase, as
    // HeaderSearch's click listener: react-native-web Pressables stop propagation.
    const onPointerDown = (event: {target?: unknown}) => {
      const nodes = [
        accountScrollRef.current,
        accountPanelRef.current,
        accountBackdropRef.current,
        accountButtonRef.current,
      ];
      if (!nodes.some(node => containsTarget(node, event.target))) {
        setAccountOpen(false);
      }
    };
    const doc = document;
    doc.addEventListener('keydown', onKeyDown);
    doc.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      doc.removeEventListener('keydown', onKeyDown);
      doc.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [accountOpen]);

  // The rows paint the ground, not the root: the header box keeps its full height
  // (spec § 7.2) and a hidden row leaves it transparent.
  return (
    <View
      testID="site-header-mobile"
      style={styles.root}
      pointerEvents="box-none"
      onLayout={e => setHeaderWidth(e.nativeEvent.layout.width)}>
      {searchOpen && searchRowShown ? (
        // First child, so the rows and the panel paint over it. From the box's bottom edge, the
        // search row's: the box keeps its full height in every state (spec § 7.2).
        <Pressable
          ref={backdropRef}
          testID="site-header-mobile-search-backdrop"
          accessible={false}
          focusable={false}
          style={[styles.backdrop, {height: windowHeight}]}
        />
      ) : null}
      {searchShown ? (
        // Mounted whenever search shows, compact or not: the row's 60 is part of the 152 box
        // that heightsFor('mobile', 'ecom') and useHeaderHeight() report. Below strap and nav,
        // so the search row slides up behind them.
        <Animated.View
          testID="site-header-mobile-search-row"
          style={[
            styles.searchRow,
            {
              opacity: searchProgress,
              transform: [
                {
                  translateY: searchProgress.interpolate({
                    inputRange: [0, 1],
                    outputRange: [-SEARCH_ROW_HEIGHT, 0],
                  }),
                },
              ],
            },
          ]}
          {...hiddenWhen(!searchRowShown)}>
          {/* DIS-1907: a type-ahead field; the suggestion panel hangs below it at its width. */}
          <HeaderSearch
            testID="site-header-mobile-search"
            style={styles.searchField}
            textStyle={styles.searchText}
            // The panel hangs from the search row, the bottom of the 152: the window below it.
            // Assumption, as DesktopHeader's panelMaxHeight: the header sits at the window top.
            panelMaxHeight={windowHeight - heightsFor('mobile', 'ecom').full}
            onSubmit={actions.onSearchSubmit}
            onResultPress={actions.onSearchResultPress}
            onOpenChange={setSearchOpen}
            backdropRef={backdropRef}
          />
        </Animated.View>
      ) : null}
      {/* The compact shadow, faded with opacity instead of animating box-shadow. */}
      <Animated.View style={[styles.shadow, {opacity: shadowProgress}]} {...hiddenWhen(true)} />
      <View style={styles.bars}>
        {showStoreSelector && giftCardStore ? (
          // R42: MY_STORE is not registered for a gift-card store; plain text, no chevron.
          <View testID="site-header-mobile-strap" style={styles.strap}>
            <View style={styles.strapText}>
              {actions.onBackToStorePress ? (
                <BackToStoreButton onPress={actions.onBackToStorePress} />
              ) : null}
              <Text style={styles.strapName}>{GIFT_CARD_STORE_COPY}</Text>
            </View>
          </View>
        ) : showStoreSelector ? (
          <Pressable
            testID="site-header-mobile-strap"
            onPress={actions.onStorePress}
            style={styles.strap}
            accessibilityRole="button"
            accessibilityLabel={`Store, ${storeLabel}`}>
            <View testID="site-header-mobile-strap-text" style={styles.strapText}>
              <Text style={styles.strapName} numberOfLines={1}>
                {storeLabel}
              </Text>
              {storeName ? (
                <Text style={styles.strapAvailability} numberOfLines={1}>
                  {AVAILABILITY_COPY[ecom ? availability : 'inStore']}
                </Text>
              ) : null}
            </View>
            <ChevronRightImg
              viewBox={`0 0 ${CHEVRON} ${CHEVRON}`}
              width={CHEVRON}
              height={CHEVRON}
              color={headerChrome.onGround}
              style={styles.fixed}
              aria-hidden={true}
            />
          </Pressable>
        ) : (
          // The band keeps its 40: it is part of heightsFor('mobile', variant).
          <View testID="site-header-mobile-strap" style={styles.strap} />
        )}
        <View testID="site-header-mobile-nav" style={styles.nav}>
          <View style={styles.navLeft}>
            {/* DIS-2010: in-store-only has no online shopping, so no menu (Figma 77:5858 variant 3). */}
            {showMenuButton && !inStoreOnly ? (
              <Pressable
                testID="site-header-mobile-menu"
                onPress={onMenuPress}
                accessibilityRole="button"
                accessibilityLabel="Menu">
                <HamburgerImg
                  viewBox={`0 0 ${MENU_ICON} ${MENU_ICON}`}
                  width={MENU_ICON}
                  height={MENU_ICON}
                  color={headerChrome.onGround}
                />
              </Pressable>
            ) : null}
            {giftCardStore ? (
              // R42: HOME is not registered for a gift-card store, so the logo is not a link.
              <View testID="site-header-mobile-logo">
                <HeaderLogo height={MOBILE_LOGO_HEIGHT} />
              </View>
            ) : (
              <Pressable
                testID="site-header-mobile-logo"
                onPress={actions.onLogoPress}
                accessibilityRole="link"
                accessibilityLabel="Home">
                <HeaderLogo height={MOBILE_LOGO_HEIGHT} />
              </Pressable>
            )}
          </View>
          <View style={styles.navRight}>
            {searchShown ? (
              // Out of the row's flow, left of the account icon: the full header (65:4145)
              // keeps its icon positions while the search icon is hidden.
              <Animated.View
                style={[styles.searchIcon, {opacity: searchIconProgress}]}
                {...hiddenWhen(!compact)}>
                <Pressable
                  testID="site-header-mobile-search-icon"
                  onPress={actions.onSearchPress}
                  accessibilityRole="button"
                  accessibilityLabel="Search">
                  <SearchIconImg
                    viewBox={`0 0 ${SEARCH_ICON} ${SEARCH_ICON}`}
                    width={COMPACT_SEARCH_ICON}
                    height={COMPACT_SEARCH_ICON}
                    color={headerChrome.onGround}
                  />
                </Pressable>
              </Animated.View>
            ) : null}
            {showUtilities ? (
              // DIS-1907: the press toggles the account panel (Figma 254:9734).
              <Animated.View
                testID="site-header-mobile-account"
                style={{opacity: accountProgress}}
                {...hiddenWhen(!accountShown)}>
                <Pressable
                  ref={accountButtonRef}
                  testID="site-header-mobile-account-button"
                  onPress={() =>
                    account.isLoggedIn ? setAccountOpen(open => !open) : account.onGuestPress()
                  }
                  style={styles.iconBox}
                  accessibilityRole="button"
                  accessibilityLabel="Account"
                  // aria-expanded, not accessibilityState: react-native-web 0.21 drops the latter.
                  aria-expanded={account.isLoggedIn ? accountOpen : undefined}>
                  {account.isLoggedIn && account.initials ? (
                    // Figma 254:9737 node 77:6036: the initials on a 28 white circle.
                    <AccountAvatar
                      initials={account.initials}
                      size={ICON_BOX}
                      testID="site-header-mobile-account-avatar"
                    />
                  ) : (
                    <AccountImg
                      testID="site-header-mobile-account-icon"
                      viewBox={ACCOUNT_ICON_VIEWBOX}
                      width={MOBILE_ACCOUNT_ICON}
                      height={MOBILE_ACCOUNT_ICON}
                      color={headerChrome.onGround}
                      aria-hidden={true}
                    />
                  )}
                </Pressable>
              </Animated.View>
            ) : null}
            {ecom && showUtilities && showCart ? (
              <Pressable
                testID="site-header-mobile-cart"
                onPress={actions.onCartPress}
                style={styles.cart}
                accessibilityRole="button"
                accessibilityLabel={`Cart, ${cartCount} items`}>
                <MobileCartImg
                  viewBox={`0 0 ${CART_WIDTH} ${ICON_BOX}`}
                  width={CART_WIDTH}
                  height={ICON_BOX}
                  color={headerChrome.onGround}
                />
                <View style={styles.badge}>
                  <Text style={styles.badgeText}>{cartCount}</Text>
                </View>
              </Pressable>
            ) : null}
          </View>
        </View>
      </View>
      {accountOpen ? (
        <>
          {/* Figma 254:9745: the overlay dims the page below the visible header. */}
          <Pressable
            ref={accountBackdropRef}
            testID="site-header-mobile-account-backdrop"
            accessible={false}
            focusable={false}
            onPress={() => setAccountOpen(false)}
            style={[
              styles.backdrop,
              styles.accountLayer,
              {top: accountTop, height: windowHeight - accountTop},
            ]}
          />
          {/* Scrolls when the rows outrun the window (a short phone, logged in). */}
          <ScrollView
            ref={accountScrollRef}
            testID="site-header-mobile-account-scroll"
            style={[styles.accountScroll, {top: accountTop, maxHeight: windowHeight - accountTop}]}>
            <AccountPanel
              panelRef={accountPanelRef}
              firstName={account.firstName}
              items={account.items}
              onBannerPress={account.onBannerPress}
              metrics={mobileAccountMetrics(headerWidth ?? windowWidth)}
              onClose={() => setAccountOpen(false)}
            />
          </ScrollView>
        </>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  // Full height in every scroll state: the search row is in flow, painted or not.
  root: {position: 'relative', zIndex: 10, paddingTop: STRAP_HEIGHT + NAV_HEIGHT},
  // SHORTCUT: black at 30 % is DesktopHeader's BACKDROP_OPACITY (Figma 346:31660 #000 @ 30 %);
  // no overlay token. Upgrade path: DIS-1735 adds an overlay token and both read it.
  backdrop: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: universal.black,
    opacity: 0.3,
  },
  bars: {position: 'absolute', top: 0, left: 0, right: 0, zIndex: 3},
  // Above the search row (1), the shadow (2) and the bars (3); the panel paints after the
  // backdrop.
  accountLayer: {zIndex: 4},
  accountScroll: {position: 'absolute', left: space.s16, right: space.s16, zIndex: 4},
  shadow: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: STRAP_HEIGHT + NAV_HEIGHT,
    zIndex: 2,
    boxShadow: SHADOW,
  },
  // Figma draws the strap borders 0.5 (verdict "design error: half pixel"); 1 is used.
  strap: {
    height: STRAP_HEIGHT,
    backgroundColor: headerChrome.ground,
    paddingHorizontal: space.s12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderTopWidth: control.borderWidth,
    borderBottomWidth: control.borderWidth,
    borderColor: headerChrome.divider,
  },
  // G7-D1: the texts shrink and ellipsise so a long store name never widens the strap past the
  // viewport; the chevron keeps its 16 (`fixed`).
  strapText: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.s8,
    flexShrink: 1,
    minWidth: 0,
  },
  strapName: {...chromeType.label13Medium, color: headerChrome.onGround, flexShrink: 1},
  strapAvailability: {...chromeType.body13Light, color: headerChrome.onGround, flexShrink: 1},
  // A web svgr <svg> is a CSS flex item, which shrinks by default (flex-shrink: 1).
  fixed: {flexShrink: 0},
  // Figma: hamburger at x 15, logo at x 52, icons end 15.67 from the right; 16 / 12 / 16 used.
  nav: {
    height: NAV_HEIGHT,
    backgroundColor: headerChrome.ground,
    paddingHorizontal: space.s16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  navLeft: {flexDirection: 'row', alignItems: 'center', gap: space.s12},
  // Figma icon gap 18.67 is the 7/6 scale artefact; space.s16 is used.
  navRight: {flexDirection: 'row', alignItems: 'center', gap: space.s16},
  searchIcon: {position: 'absolute', right: '100%', marginRight: space.s16},
  iconBox: {width: ICON_BOX, height: ICON_BOX, alignItems: 'center', justifyContent: 'center'},
  cart: {width: CART_WIDTH, height: ICON_BOX},
  // Figma 229:6121: the badge sits on the cart's lower left. Colours follow CartCountBadge.
  badge: {
    position: 'absolute',
    left: 0,
    top: BADGE_TOP,
    height: BADGE_HEIGHT,
    paddingHorizontal: space.s4,
    borderRadius: BADGE_HEIGHT / 2,
    justifyContent: 'center',
    backgroundColor: theme.colour.general.secondary,
  },
  badgeText: {...chromeType.badge9Regular, color: theme.colour.general.primary},
  // Figma field is 346 wide at 375 (x 14); the 16 gutter gives 343, matching the menu column.
  searchRow: {
    position: 'relative',
    zIndex: 1,
    height: SEARCH_ROW_HEIGHT,
    backgroundColor: headerChrome.ground,
    paddingTop: SEARCH_TOP,
    paddingHorizontal: space.s16,
  },
  searchField: {
    height: SEARCH_FIELD_HEIGHT,
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.s8,
    paddingHorizontal: field.paddingX,
    borderRadius: radius.control,
    backgroundColor: universal.white,
  },
  // SHORTCUT: 16, not Figma's 15 (body15Regular): iOS Safari zooms the page on focus of an input
  // under 16 px, the floor ui-controls TextInput's `webMinFontSize` applies. Ceiling: 1 px larger
  // than Figma. Upgrade path: design moves the mobile field to 16.
  searchText: {...chromeType.body15Regular, fontSize: 16},
});
